/**
 * COMP-GRIFO y COMP-BAJOENC se enlazan a la encimera del fregadero
 * solo cuando el acabado de esa encimera es Compacto.
 * El listado numera por firstParentID: encimera 9.1 → 9.1.1 y 9.1.2.
 * No se meten en `childs` (eso montaría otra malla en el 3D).
 */
import { isCompactoFinish } from '@utils/sceneObjects/frontals/frontalRuns'
import { itemUsesAccesorioFregaderoCatalog } from '@utils/sceneObjects/isAccesorioFregaderoCatalog'
import { getEncimeraTextureEntry } from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'

type LinkItem = {
  id?: string
  type?: string
  catalogID?: string | number | null
  codigo?: string | null
  referencia?: string | null
  description?: string | null
  typeOfSon?: string | null
  firstParentID?: string
  depth?: number
  textures?: Array<{ zone?: string; zoneDisplayName?: string; acabado?: string }>
  childs?: { forEach: (fn: (id: string) => void) => void } | null
}

const COMPACT_ACCESSORY_ORDER = ['COMP-GRIFO', 'COMP-BAJOENC'] as const

export function compactAccessoryRef(item: LinkItem | null | undefined): string {
  return String(item?.catalogID ?? item?.codigo ?? item?.referencia ?? '')
    .trim()
    .toUpperCase()
}

export function compactAccessoryRank(item: LinkItem | null | undefined): number {
  const ref = compactAccessoryRef(item)
  const index = COMPACT_ACCESSORY_ORDER.findIndex((code) => ref.startsWith(code))
  return index === -1 ? 100 : index
}

export function isWorktopLinkedSon(item: LinkItem | null | undefined): boolean {
  if (!item || item.type !== 'linkedSon') return false
  if (itemUsesAccesorioFregaderoCatalog(item)) return false
  if (String(item.typeOfSon ?? '') === 'Encimera') return true
  return String(item.description ?? '').trim().toLowerCase() === 'encimera'
}

function findWorktop(items: Map<string, LinkItem>, sinkId: string): LinkItem | undefined {
  const sink = items.get(sinkId)
  let found: LinkItem | undefined
  const consider = (child: LinkItem | undefined) => {
    if (!found && child && isWorktopLinkedSon(child)) found = child
  }
  sink?.childs?.forEach((id) => consider(items.get(String(id))))
  if (found) return found
  items.forEach((child) => {
    if (child?.firstParentID === sinkId) consider(child)
  })
  return found
}

export function collectCompactAccessoryIds(
  items: Map<string, any>,
  sinkId: string,
): { bajoEnc: string[]; grifo: string[] } {
  const bajoEnc: string[] = []
  const grifo: string[] = []
  const seen = new Set<string>()
  const worktopId = findWorktop(items, sinkId)?.id
  const accepted = new Set<string>([sinkId])
  if (worktopId) accepted.add(worktopId)

  items.forEach((child, id) => {
    if (!child || !accepted.has(String(child.firstParentID ?? ''))) return
    if (!itemUsesAccesorioFregaderoCatalog(child)) return
    if (seen.has(id)) return
    seen.add(id)
    const ref = compactAccessoryRef(child)
    if (ref.startsWith('COMP-BAJOENC')) bajoEnc.push(id)
    else if (ref.startsWith('COMP-GRIFO')) grifo.push(id)
  })

  return { bajoEnc, grifo }
}

/** Mueve los accesorios ya creados bajo la encimera compacta. Idempotente. */
export function attachCompactAccessoriesToEncimera(items: Map<string, any>): boolean {
  let changed = false
  const sinks: LinkItem[] = []
  items.forEach((item) => {
    if (item?.type === 'linkedItem' && item.id) sinks.push(item)
  })

  for (const sink of sinks) {
    const worktop = findWorktop(items, String(sink.id))
    if (!worktop?.id) continue
    if (!isCompactoFinish(getEncimeraTextureEntry(worktop)?.acabado)) continue

    const { grifo, bajoEnc } = collectCompactAccessoryIds(items, String(sink.id))
    for (const id of [...grifo, ...bajoEnc]) {
      const accessory = items.get(id)
      if (!accessory) continue
      if (accessory.firstParentID !== worktop.id) {
        accessory.firstParentID = worktop.id
        changed = true
      }
      if (accessory.depth !== 2) {
        accessory.depth = 2
        changed = true
      }
    }
  }

  return changed
}

/** Para la consola: dónde cae cada COMP dentro del árbol que sale a SignalR o al presupuesto. */
export function describeCompactAccessoryLinks(unidades: readonly any[]): string[] {
  const rows: string[] = []
  const walk = (node: any, parent: string) => {
    if (!node) return
    const idUnidades = Number(node.IdUnidades ?? node.idUnidades ?? 0)
    const id = String(node.IdFEUnidad ?? node.idFEUnidad ?? '')
    const enlaces = node.Enlaces ?? node.enlaces ?? []
    if (idUnidades === 7704 || idUnidades === 7703) {
      const name = idUnidades === 7704 ? 'COMP-GRIFO' : 'COMP-BAJOENC'
      rows.push(`${name} (${id}) dentro de ${parent}`)
    }
    const here = id ? `${id} / idUnidades ${idUnidades || '?'}` : parent
    if (Array.isArray(enlaces)) {
      for (const child of enlaces) walk(child, here)
    }
  }
  for (const unidad of unidades) walk(unidad, 'raíz del mueble')
  return rows
}
